import {
  BodyMedium,
  HeadingLarge,
  SubtleButton,
  Tabs,
  theme,
  waitFor,
  DeprecatedChevronLeftIcon,
} from '@arnold/common';
import { Modal } from '@arnold/common/lib/components/Modal/Modal';
import styled from '@emotion/styled';
import {
  CompactSelection,
  DataEditor,
  EditableGridCell,
  GridCell,
  GridCellKind,
  GridColumn,
  GridSelection,
  ImageWindowLoader,
  Item,
  Rectangle,
  Theme,
} from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, FormControl } from 'react-bootstrap';
import { TFunction, Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import sanitize from 'sanitize-filename';
import * as XLSX from 'xlsx';
import { FormErrorFeedback, FormGroupLabel, LinkButton } from '../../components/Common';
import { DangerZoneWarning } from '../../components/Forms/RespondentForm/RespondentForm';
import {
  ContactSource,
  ContactType,
  LanguagesQuery,
  OrganizationQuery,
  OrganizationTeamsQuery,
  SurveyGroupsQuery,
  SurveysQuery,
  useVerifyPhoneNumbersMutation,
} from '../../generated/hooks';
import { StyledFormGroup } from '../PreviewForm/styles';
import { flatTeams } from '../Teams/TeamsAndPeople';
import { constructTeamStructure } from '../Teams/TeamsScreen';
import { ImportTab } from './constants';
import { EmployeeValidationPanel } from './EmployeeValidationPanel';
import { OrganizationImportSaveModals } from './OrganizationImportSaveModals';
import {
  createAdditionalDataForEmployees,
  createAdditionalDataforTeams,
  EmployeeImportState,
  TeamImportState,
} from './OrgImportUtils';
import { filterEmptyEmployees, filterEmptyTeams } from './utils';
import { validatePhoneNumbers } from './validation';

export type Position = {
  row: number;
  column: number;
};
export type ValidationError = {
  position: Position[];
  message: string;
  messageObject?: any;
  tab: string;
};

// Add column width tracking interface
interface ColumnWidths {
  [columnId: string]: number;
}

export interface EmployeeRecord {
  firstname: string;
  surname: string;
  team: string;
  email: string;
  otherEmail: string;
  countryCallingCode: string;
  phoneNumber: string;
  preferredContact: 'EMAIL' | 'SMS' | string;
  surveyParticipant: string;
  managerOfTeam: string[];
  language: string;
}

export interface EmployeeRecordWithId extends EmployeeRecord {
  id: string;
}

export interface TeamRecord {
  id: string;
  name: string;
  supervisorTeam: string;
}

interface IProps {
  organization: NonNullable<OrganizationQuery['organization']>;
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>;
  surveyGroups: NonNullable<SurveyGroupsQuery['organization']>['surveyGroups'];
  surveys: NonNullable<SurveysQuery['user']['organization']>['surveys'];
  refetch: () => void;
  languages: LanguagesQuery['languages'];
  userId: string;
}

// Cache for storing phone number validation results
const phoneNumberValidationCache = new Map<string, boolean>();

const newEmployee = {
  firstname: '',
  surname: '',
  team: '',
  email: '',
  otherEmail: '',
  countryCallingCode: '',
  phoneNumber: '',
  preferredContact: 'EMAIL',
  surveyParticipant: 'Y',
  managerOfTeam: [],
  language: '',
};

const newTeam = {
  id: '',
  name: '',
  supervisorTeam: '',
};

export const OrganizationImport = ({
  organization,
  organizationTeams,
  surveyGroups,
  refetch,
  surveys,
  languages,
  userId,
}: IProps) => {
  const [t] = useTranslation('organizationImportCard');
  const [excelTrans] = useTranslation('organizationExcel');
  const history = useHistory();
  const [showTable, setShowTable] = useState(false);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [teams, setTeams] = useState<TeamRecord[]>([]);
  const [selectedTab, setSelectedTab] = useState<ImportTab>(ImportTab.EMPLOYEES);
  const [saveModalsShow, setSaveModalsShow] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [selection, setSelection] = useState<GridSelection | undefined>(undefined);
  const [verifyPhoneNumbersLoading, setVerifyPhoneNumbersLoading] = useState(false);
  const gridRef = useRef<any>(null);
  // State to track column widths for both tabs
  const [employeeColumnWidths, setEmployeeColumnWidths] = useState<ColumnWidths>({});
  const [teamColumnWidths, setTeamColumnWidths] = useState<ColumnWidths>({});
  const [originalEmployees, originalTeams] = useMemo(
    () => [constructEmployees(organizationTeams, false), constructTeams(organizationTeams)],
    [organizationTeams],
  );
  const [worker, setWorker] = useState<Worker | null>(null);

  const [finalStep, setFinalStep] = useState(false);
  const [importFileErrorModalShow, setImportFileErrorModalShow] = useState(false);

  const changedEmployeesCount = useMemo(() => {
    const employeesWithAdditionalData = createAdditionalDataForEmployees(
      filterEmptyEmployees(employees),
      organizationTeams,
    );
    const employeesCreatedCount = employeesWithAdditionalData.filter(
      (employee) => employee.state === EmployeeImportState.New,
    ).length;

    const employeesEditedCount = employeesWithAdditionalData.filter(
      (employee) => employee.state === EmployeeImportState.Edited || employee.state === EmployeeImportState.NotSure,
    ).length;
    const employeesUnchangedCount = employeesWithAdditionalData.filter(
      (employee) => employee.state === EmployeeImportState.Unchanged,
    ).length;

    const employeesDeletedCount = originalEmployees.length - employeesEditedCount - employeesUnchangedCount;

    return employeesCreatedCount + employeesEditedCount + employeesDeletedCount;
  }, [employees, organizationTeams, originalEmployees.length]);

  const changedTeamsCount = useMemo(() => {
    const teamsWithAdditionalData = createAdditionalDataforTeams(filterEmptyTeams(teams), organizationTeams);
    const teamsCreatedCount = teamsWithAdditionalData.filter((team) => team.state === TeamImportState.New).length;
    const teamsEditedCount = teamsWithAdditionalData.filter((team) => team.state === TeamImportState.Edited).length;
    const teamsUnchangedCount = teamsWithAdditionalData.filter(
      (team) => team.state === TeamImportState.Unchanged,
    ).length;

    const teamssDeletedCount = originalTeams.length - teamsEditedCount - teamsUnchangedCount;
    return teamsCreatedCount + teamsEditedCount + teamssDeletedCount;
  }, [teams, organizationTeams, originalTeams.length]);

  const [importFileNameMismatchModalData, setImportFileNameMismatchModalData] = useState({
    show: false,
    fileName: '',
    confirmCallback: (confirmed: boolean) => {
      return;
    },
  });
  useEffect(() => {
    setEmployees(constructEmployees(organizationTeams, false));
  }, [organizationTeams]);

  useEffect(() => {
    setTeams(constructTeams(organizationTeams));
  }, [organizationTeams]);

  useEffect(() => {
    const newWorker = new Worker(new URL('./validationWorker.ts', import.meta.url));

    // Set up message handler for the worker
    newWorker.onmessage = (event) => {
      const data = event.data;

      if (data.errors) {
        setErrors((prevErrors) => {
          // Get all the phone number errors - these are not validated by the worker and we need to preserve them
          const phoneNumberErrors = prevErrors.filter(
            (err) => err.message === 'invalidPhoneNumber' && err.tab === ImportTab.EMPLOYEES,
          );

          // Combine worker errors with phone errors
          return [...data.errors, ...phoneNumberErrors];
        });
      }

      if (data.error) {
        console.error('Worker error:', data.error);
      }
    };

    newWorker.onerror = (error) => {
      console.error('Worker error:', error);
    };

    setWorker(newWorker);

    // Clean up worker on unmount
    return () => {
      newWorker.terminate();
    };
  }, []);

  const [verifyPhoneNumbers] = useVerifyPhoneNumbersMutation();

  const validateEmployeePhoneNumbers = useCallback(async () => {
    const nonEmptyEmployees = filterEmptyEmployees(employees);
    const phoneNumbers = nonEmptyEmployees
      .filter((emp) => emp.phoneNumber)
      .map((emp) => `+${emp.countryCallingCode}${emp.phoneNumber}`.replace('++', '+'));

    if (phoneNumbers.length === 0) return;

    // Check which numbers we need to validate via API
    const numbersToCheck = phoneNumbers.filter((phone) => !phoneNumberValidationCache.has(phone));

    if (numbersToCheck.length > 0) {
      setVerifyPhoneNumbersLoading(true);
      // Call API only for numbers not in cache and update cache
      await verifyPhoneNumbers({
        variables: { phoneNumbers: numbersToCheck },
      })
        .then((data) => {
          data.data?.verifyPhoneNumbers.forEach((result) => {
            phoneNumberValidationCache.set(result.specifiedPhoneNumber, !!result.phoneNumber);
          });
        })
        .finally(() => {
          setVerifyPhoneNumbersLoading(false);
        });
    }

    const validPhoneNumbers = Array.from(phoneNumberValidationCache.entries())
      .filter(([_, isValid]) => isValid)
      .map(([phoneNumber]) => phoneNumber);

    const phoneErrors = validatePhoneNumbers(filterEmptyEmployees(employees), validPhoneNumbers);

    setErrors((prevErrors) => {
      // Filter out any previous phone number errors
      const otherErrors = prevErrors.filter(
        (err) => !(err.message === 'invalidPhoneNumber' && err.tab === ImportTab.EMPLOYEES),
      );
      return [...otherErrors, ...phoneErrors];
    });
  }, [employees, verifyPhoneNumbers]);

  useEffect(() => {
    if (worker) {
      worker.postMessage({
        employees,
        teams,
        languages,
      });
      validateEmployeePhoneNumbers();
    }
  }, [worker, employees, teams, languages, validateEmployeePhoneNumbers]);

  const downloadExcelClicked = () => {
    // we want to only export the org structure that is saved in the DB not the one being edited
    let savedEmployees = constructEmployees(organizationTeams, false);
    let savedTeams = constructTeams(organizationTeams);

    // If no employees, add a dummy employee record to ensure headers are generated
    if (savedEmployees.length === 0) {
      savedEmployees = [{ ...newEmployee, preferredContact: '' }];
    }

    // If no teams, add a dummy team record to ensure headers are generated
    if (savedTeams.length === 0) {
      savedTeams = [{ ...newTeam }];
    }

    createAndDownloadOrganizationImportFile(savedEmployees, savedTeams, organization.systemName, excelTrans);
  };

  const employeeColumns = useMemo<GridColumn[]>(() => {
    return [
      {
        title: excelTrans('employeeSheetFirstname'),
        id: 'firstname',
        width: employeeColumnWidths.firstname || getOrganizationImportColumnWidth('firstname'),
      },
      {
        title: excelTrans('employeeSheetSurname'),
        id: 'surname',
        width: employeeColumnWidths.surname || getOrganizationImportColumnWidth('surname'),
      },
      {
        title: excelTrans('employeeSheetTeam'),
        id: 'team',
        width: employeeColumnWidths.team || getOrganizationImportColumnWidth('team'),
      },
      {
        title: excelTrans('employeeSheetEmail'),
        id: 'email',
        width: employeeColumnWidths.email || getOrganizationImportColumnWidth('email'),
      },
      {
        title: excelTrans('employeeSheetOtherEmail'),
        id: 'otherEmail',
        width: employeeColumnWidths.otherEmail || getOrganizationImportColumnWidth('otherEmail'),
      },
      {
        title: excelTrans('employeeSheetCountryCallingCode'),
        id: 'countryCallingCode',
        width: employeeColumnWidths.countryCallingCode || getOrganizationImportColumnWidth('countryCallingCode'),
      },
      {
        title: excelTrans('employeeSheetPhoneNumber'),
        id: 'phoneNumber',
        width: employeeColumnWidths.phoneNumber || getOrganizationImportColumnWidth('phoneNumber'),
      },
      {
        title: excelTrans('employeeSheetPreferredContact'),
        id: 'preferredContact',
        width: employeeColumnWidths.preferredContact || getOrganizationImportColumnWidth('preferredContact'),
      },
      {
        title: excelTrans('employeeSheetSurveyParticipant'),
        id: 'surveyParticipant',
        width: employeeColumnWidths.surveyParticipant || getOrganizationImportColumnWidth('surveyParticipant'),
      },
      {
        title: excelTrans('employeeSheetManagerOfTeam'),
        id: 'managerOfTeam',
        width: employeeColumnWidths.managerOfTeam || getOrganizationImportColumnWidth('managerOfTeam'),
      },
      {
        title: excelTrans('employeeSheetLanguage', { languages: organization.languages.map((l) => l.code).join('/') }),
        id: 'language',
        width: employeeColumnWidths.language || getOrganizationImportColumnWidth('language'),
      },
    ];
  }, [excelTrans, employeeColumnWidths, organization.languages]);

  const teamColumns = useMemo<GridColumn[]>(() => {
    return [
      {
        title: excelTrans('teamSheetId'),
        id: 'id',
        width: teamColumnWidths.id || getOrganizationImportColumnWidth('id'),
      },
      {
        title: excelTrans('teamSheetName'),
        id: 'name',
        width: teamColumnWidths.name || getOrganizationImportColumnWidth('name'),
      },
      {
        title: excelTrans('teamSheetSupervisorTeam'),
        id: 'supervisorTeam',
        width: teamColumnWidths.supervisorTeam || getOrganizationImportColumnWidth('supervisorTeam'),
      },
    ];
  }, [excelTrans, teamColumnWidths]);

  const activeColumns = useMemo(() => {
    return selectedTab === ImportTab.EMPLOYEES ? employeeColumns : teamColumns;
  }, [selectedTab, employeeColumns, teamColumns]);

  const activeData = useMemo(() => {
    if (selectedTab === ImportTab.EMPLOYEES) {
      return employees;
    } else {
      return teams;
    }
  }, [selectedTab, employees, teams]);

  // Function to get cell data that is used by the DataEditor
  const getCellContent = useCallback(
    ([col, row]: Item): GridCell => {
      const data = activeData[row];
      const columnId = activeColumns[col].id as string;
      let value = (data[columnId as keyof typeof data] || '') as string | string[];

      // Handle different column types
      if (columnId === 'surveyParticipant') {
        return {
          kind: GridCellKind.Boolean,
          allowOverlay: false,
          readonly: false,
          data: ['Y', 'YES', 'TRUE'].includes(String(value).toUpperCase()),
        };
      }

      // Handle managerOfTeam array - display as semicolon-separated string
      if (columnId === 'managerOfTeam' && Array.isArray(value)) {
        value = value.join('; ');
      }

      return {
        kind: GridCellKind.Text,
        allowOverlay: true,
        readonly: false,
        displayData: String(value),
        data: value as string,
        // overide styles for selected error cell
        themeOverride: {
          bgCell: errors.find((error) =>
            error.position.some(
              ({ row: errorRow, column }) => errorRow === row && column === col && error.tab === selectedTab,
            ),
          )
            ? theme.colors.emotionDanger.backgroundActive
            : 'white',
        },
      };
    },
    [activeData, activeColumns, errors, selectedTab],
  );

  const drawCell = useCallback(
    (
      args: {
        ctx: CanvasRenderingContext2D;
        cell: GridCell;
        theme: Theme;
        rect: Rectangle;
        col: number;
        row: number;
        hoverAmount: number;
        hoverX: number | undefined;
        hoverY: number | undefined;
        highlighted: boolean;
        imageLoader: ImageWindowLoader;
      },
      drawContent: () => void,
    ) => {
      const { ctx, rect, row, col } = args;
      const { x, y, width, height } = rect;

      if (
        errors.find((error) =>
          error.position.some(
            ({ row: errorRow, column }) => errorRow === row && column === col && error.tab === selectedTab,
          ),
        )
      ) {
        ctx.strokeStyle = theme.colors.emotionDanger.active;
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 0.75, y + 1.5, width - 1.25, height - 2);
      }

      drawContent();
    },
    [errors, selectedTab],
  );

  const scrollToCell = (x: number, y: number) => {
    // focus selected cell
    setSelection({
      ...selection!,
      current: {
        cell: [x, y],
        range: { x, y, width: 1, height: 1 },
        rangeStack: [],
      },
      columns: CompactSelection.empty(),
      rows: CompactSelection.empty(),
    });

    if (gridRef.current) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          gridRef.current?.scrollTo(x, y);
        }, 30);
      });
    }
  };

  // Handle cell edit
  const editCell = useCallback(
    ([col, row]: Item, updatedValue: any) => {
      const columnId = activeColumns[col].id as string;

      // Handle managerOfTeam - convert semicolon-separated string back to array
      if (columnId === 'managerOfTeam' && typeof updatedValue === 'string') {
        updatedValue = updatedValue
          .split(';')
          .map((team: string) => team.trim())
          .filter((team: string) => team !== '');
      }

      if (selectedTab === ImportTab.EMPLOYEES) {
        setEmployees((oldData) => {
          const copyOfData = [...oldData];
          const updatedItem = { ...copyOfData[row], [columnId]: updatedValue };
          copyOfData[row] = updatedItem;
          return copyOfData;
        });
      } else {
        setTeams((oldData) => {
          const copyOfData = [...oldData];
          const updatedItem = { ...copyOfData[row], [columnId]: updatedValue };
          copyOfData[row] = updatedItem;
          return copyOfData;
        });
      }
    },
    [activeColumns, selectedTab],
  );
  const onCellEdited = useCallback(
    ([col, row]: Item, newValue: EditableGridCell) => {
      let updatedValue = newValue.data;
      if (newValue.kind === GridCellKind.Boolean) {
        updatedValue = newValue.data ? 'Y' : 'N';
      }
      editCell([col, row], updatedValue);
    },
    [editCell],
  );

  const handleExcelImport = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });

        // Ignore the first sheet (help), only take the second (employees) and third (teams)
        const employeesSheet = workbook.SheetNames[1];
        const teamsSheet = workbook.SheetNames[2];

        if (employeesSheet && teamsSheet) {
          const employeesData = XLSX.utils.sheet_to_json(workbook.Sheets[employeesSheet], { header: 1 }).slice(1);
          // check that there is at least 1 employee
          if (employeesData.length === 0) {
            setImportFileErrorModalShow(true);
            return;
          }
          const teamsData = XLSX.utils.sheet_to_json(workbook.Sheets[teamsSheet], { header: 1 }).slice(1);

          // Convert data to the correct format
          const importedEmployees = convertExcelEmployeesToRecords(employeesData);
          const importedTeams = convertExcelTeamsToRecords(teamsData);

          setEmployees(importedEmployees);
          setTeams(importedTeams);
          setShowTable(true);
        } else {
          setImportFileErrorModalShow(true);
        }
      } catch (err) {
        console.error('Error processing XLSX file:', err);
        setImportFileErrorModalShow(true);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  // Converts employee data from Excel to the correct format
  const convertExcelEmployeesToRecords = (data: any[]): EmployeeRecord[] => {
    // Map Excel columns to keys in our format
    return data
      .filter((row) => !row.every((cell: any) => !cell))
      .map((row) => {
        // For each row, find the corresponding keys
        const employee: EmployeeRecord = {
          firstname: (row[0]?.toString() || '').trim(),
          surname: (row[1]?.toString() || '').trim(),
          team: (row[2]?.toString() || '').trim(),
          email: (row[3]?.toString() || '').trim().toLowerCase(),
          otherEmail: (row[4]?.toString() || '').trim().toLowerCase(),
          countryCallingCode: (row[5]?.toString() || '').trim(),
          phoneNumber: (row[6]?.toString() || '').replace(/\s/g, ''),
          preferredContact: (row[7]?.toString() || '').trim(),
          surveyParticipant: (row[8]?.toString() || '').trim(),
          managerOfTeam: (row[9]?.toString() || '')
            .split(';')
            .map((team: string) => team.trim())
            .filter((team: string) => team !== ''),
          language: (row[10]?.toString() || '').trim().toLowerCase(),
        };

        if (
          employee.surveyParticipant === '' ||
          employee.surveyParticipant.toUpperCase() === 'TRUE' ||
          employee.surveyParticipant.toUpperCase() === 'Y' ||
          employee.surveyParticipant.toUpperCase() === 'YES'
        ) {
          employee.surveyParticipant = 'Y';
        }

        if (!employee.preferredContact) {
          // if preferred contact is not specified in the excel sheet, assign it based on the presence of email and phone number
          const hasEmail = !!employee.email || !!employee.otherEmail;
          const hasPhoneNumber = !!employee.phoneNumber;

          if (hasEmail && !hasPhoneNumber) {
            employee.preferredContact = 'EMAIL';
          } else if (!hasEmail && hasPhoneNumber) {
            employee.preferredContact = 'SMS';
          } // If both or neither are present, preferredContact remains '' (empty string)
        }

        return employee;
      });
  };

  // Converts team data from Excel to the correct format
  const convertExcelTeamsToRecords = (data: any[]): TeamRecord[] => {
    return data
      .filter((row) => !row.every((cell: any) => !cell))
      .map((row) => {
        const team: TeamRecord = {
          id: (row[0]?.toString() || '').trim(),
          name: (row[1]?.toString() || '').trim(),
          supervisorTeam: (row[2]?.toString() || '').trim(),
        };
        return team;
      });
  };
  const onUploadClicked = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx';
    input.style.display = 'none';
    input.onchange = async (e) => {
      const files = (e.target as HTMLInputElement).files;
      if (files && files.length > 0) {
        const file = files[0];
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();
        if (fileExtension !== 'xlsx') {
          alert(t('errorInvalidFileType'));
          return;
        }
        if (!fileName.normalize('NFC').includes(organization.systemName.normalize('NFC'))) {
          const confirmed = await new Promise((resolve) => {
            setImportFileNameMismatchModalData({
              show: true,
              fileName,
              confirmCallback: resolve,
            });
          });
          setImportFileNameMismatchModalData({
            show: false,
            fileName: '',
            confirmCallback: () => {
              return;
            },
          });
          if (!confirmed) {
            return;
          }
        }
        handleExcelImport(file);
        document.body.removeChild(input);
      }
    };
    document.body.appendChild(input);
    input.click();
  };

  const saveClicked = () => {
    // TODO: either check if the data is valid or disable the button completely if it's not valid
    setSaveModalsShow(true);
  };

  const discardChanges = () => {
    setEmployees(originalEmployees);
    setTeams(originalTeams);
  };

  // Handle column resize
  const onColumnResize = useCallback(
    (column: GridColumn, newSize: number) => {
      const columnId = column.id as string;
      if (selectedTab === ImportTab.EMPLOYEES) {
        setEmployeeColumnWidths((prev) => ({
          ...prev,
          [columnId]: newSize,
        }));
      } else {
        setTeamColumnWidths((prev) => ({
          ...prev,
          [columnId]: newSize,
        }));
      }
    },
    [selectedTab],
  );

  return (
    <OrgImportWrapper showTable={showTable}>
      <HeadingLarge>{t('employeeUpdates')}</HeadingLarge>
      <LinkButton className="mb-8" onClick={() => (showTable ? setShowTable(false) : history.push('/teams'))}>
        <DeprecatedChevronLeftIcon className="mr-5" />
        {t('common:back')}
      </LinkButton>
      <Card className="p-8">
        <Modal
          show={importFileErrorModalShow}
          onHide={() => {
            setImportFileErrorModalShow(false);
          }}
          onSubmit={() => {
            setImportFileErrorModalShow(false);
          }}
          title={t('unsuccessfulExcelImportModal:Title')}
          text={
            <Trans
              i18nKey="unsuccessfulExcelImportModal:Description"
              components={[<a href="mailto:help.me@arnold-robot.com" target="_blank" rel="noreferrer" />]}
            />
          }
          buttons={{
            submit: { title: t('unsuccessfulExcelImportModal:Submit') },
          }}
        />

        {showTable ? (
          <>
            <TabsAndButtonWrapper>
              <Tabs
                replaceHistory={true}
                items={[
                  {
                    title: t('employees'),
                    active: selectedTab === ImportTab.EMPLOYEES,
                    onClick: () => {
                      setSelectedTab(ImportTab.EMPLOYEES);
                    },
                  },
                  {
                    title: t('teams'),
                    active: selectedTab === ImportTab.TEAMS,
                    onClick: () => {
                      setSelectedTab(ImportTab.TEAMS);
                    },
                  },
                ]}
                customStyles={{ marginLeft: '-16px', marginRight: '48px', padding: '8px 0' }}
              />
              <SubtleButton onClick={onUploadClicked} data-cy="upload-again-button">
                {t('uploadAgainButton')}
              </SubtleButton>
            </TabsAndButtonWrapper>
            <DataEditorContainer>
              <DataEditor
                columns={activeColumns}
                rows={activeData.length}
                getCellContent={getCellContent}
                onCellEdited={onCellEdited}
                onPaste={([c, r], values) => {
                  if (selectedTab === ImportTab.EMPLOYEES) {
                    setEmployees((prevEmployees) => {
                      const newEmployees = [...prevEmployees];
                      const totalRows = values.length + r;
                      for (let i = prevEmployees.length; i < totalRows; i++) {
                        newEmployees.push({ ...newEmployee });
                      }
                      return newEmployees;
                    });
                  } else {
                    setTeams((prevTeams) => {
                      const newTeams = [...prevTeams];
                      const totalRows = values.length + r;
                      for (let i = prevTeams.length; i < totalRows; i++) {
                        newTeams.push({ ...newTeam });
                      }
                      return newTeams;
                    });
                  }

                  for (let i = 0; i < values.length; i++) {
                    for (let j = 0; j < values[i].length; j++) {
                      // Handle special case for surveyParticipant column (boolean values)
                      if (activeColumns[c + j].id === 'surveyParticipant') {
                        const value = values[i][j];
                        // Convert boolean or string boolean values to 'Y'/'N' format
                        const formatedValue =
                          value === 'true' || value === 'TRUE' || value === 'Y' || value === 'YES' ? 'Y' : 'N';
                        editCell([c + j, r + i], formatedValue);
                      } else {
                        editCell([c + j, r + i], values[i][j]);
                      }
                    }
                  }

                  return false;
                }}
                drawCell={drawCell}
                width="100%"
                rowMarkers="number"
                onRowAppended={async () => {
                  if (selectedTab === ImportTab.EMPLOYEES) {
                    setEmployees([
                      ...employees,
                      {
                        ...newEmployee,
                      },
                    ]);
                  } else {
                    setTeams([...teams, { ...newTeam }]);
                  }
                  await waitFor(
                    () =>
                      !!document.activeElement &&
                      typeof document.activeElement.className === 'string' &&
                      document.activeElement.className.includes('gdg-input'),
                    10000,
                    50,
                    'onRowAppended',
                    true,
                  );
                  const input = document.activeElement;
                  const escEvent = new KeyboardEvent('keydown', {
                    key: 'Escape',
                    keyCode: 27, // deprecated, but still used in some older browsers
                    code: 'Escape',
                    which: 27, // deprecated too
                    bubbles: true, // needed if you want it to bubble up the DOM
                  });
                  input?.dispatchEvent(escEvent);
                  return 'bottom' as const;
                }}
                keybindings={{ search: true }}
                getCellsForSelection={true}
                freezeColumns={selectedTab === ImportTab.EMPLOYEES ? 2 : undefined}
                smoothScrollX={true}
                smoothScrollY={true}
                ref={gridRef}
                gridSelection={selection}
                onGridSelectionChange={(newSelection: GridSelection) => {
                  setSelection(newSelection);
                }}
                onColumnResize={onColumnResize}
              />
            </DataEditorContainer>
            {saveModalsShow && (
              <OrganizationImportSaveModals
                onHide={() => setSaveModalsShow(false)}
                employees={filterEmptyEmployees(employees)}
                teams={filterEmptyTeams(teams)}
                organizationTeams={organizationTeams}
                organization={organization}
                surveyGroups={surveyGroups}
                surveys={surveys}
                refetch={refetch}
                setFinalStep={setFinalStep}
              />
            )}
            <EmployeeValidationPanel
              employees={employees}
              teams={teams}
              setSelectedTab={setSelectedTab}
              scrollToCell={scrollToCell}
              numberOfChangedEmployeeRecords={changedEmployeesCount}
              numberOfChangedTeamRecords={changedTeamsCount}
              discardChanges={discardChanges}
              errors={errors}
              onSaveClicked={saveClicked}
              gridRef={gridRef}
              validationInProgress={verifyPhoneNumbersLoading}
              finalStep={finalStep}
            />
          </>
        ) : (
          <Container>
            <HelpLinkContainer>
              <Button
                variant="link"
                bsPrefix="override"
                className="link no-btn"
                onClick={() =>
                  window.open(
                    'https://intercom.help/arnold-robot/cs/articles/7826013-pridat-celou-strukturu-zamestnancu',
                    '_blank',
                  )
                }
              >
                {t('help')}
              </Button>
            </HelpLinkContainer>
            <StepsContainer>
              <StepCard className="solidBorder">
                <h3>{t('firstStep')}</h3>
                <p>{t('downloadTableWithTemplate')}</p>
                <SubtleButton
                  onClick={downloadExcelClicked}
                  data-cy="download-table-button"
                  style={{ marginTop: '20px' }}
                >
                  {t('downloadTableButton')}
                </SubtleButton>
              </StepCard>
              <StepCard>
                <h3>{t('secondStep')}</h3>
                <p>{t('uploadTableWithTemplate')}</p>
                <SubtleButton onClick={onUploadClicked} style={{ marginTop: '20px' }}>
                  {t('uploadTableButton')}
                </SubtleButton>
              </StepCard>
            </StepsContainer>
            <StyledShortcutDescription className="mt-4">{t('shortcutToTablePreview')}</StyledShortcutDescription>
            <Button
              variant="link"
              bsPrefix="override"
              className="link no-btn mt-2 mb-4"
              data-cy="bulk-edit-button"
              onClick={() => setShowTable(true)}
            >
              {t('bulkEdit')}
            </Button>
          </Container>
        )}
      </Card>

      <ImportFileNameMismatchModal
        show={importFileNameMismatchModalData.show}
        confirmCallback={importFileNameMismatchModalData.confirmCallback}
        orgSystemName={organization.systemName}
        fileName={importFileNameMismatchModalData.fileName}
      />
    </OrgImportWrapper>
  );
};

const OrgImportWrapper = styled.div<{ showTable: boolean }>`
  max-width: ${(props) => (props.showTable ? '100%' : '928px')};
  transition: max-width 0.5s;
  margin: 0 auto;
  margin-bottom: -90px;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const StepsContainer = styled.div`
  display: flex;
  justify-content: space-around;
  gap: 32px;
  width: 100%;
`;

const HelpLinkContainer = styled.div`
  align-self: flex-end;
  margin: 8px 0;
`;

const StepCard = styled.div`
  margin: 32px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px dashed ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
  padding: 48px;
  flex-basis: 50%;
  text-align: center;

  &.solidBorder {
    border: 1px solid ${theme.colors.borderMain.default};
  }
`;

const StyledShortcutDescription = styled.p`
  font-weight: bold;
  color: ${theme.colors.text.primary};
  margin-bottom: 8px;
`;

const TabsAndButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const DataEditorContainer = styled.div`
  width: 100%;
  height: calc(100vh - 430px);
  overflow: hidden;
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
`;

interface ImportFileNameMismatchModalProps {
  show: boolean;
  confirmCallback: (confirmed: boolean) => void;
  orgSystemName: string;
  fileName: string;
}

const ImportFileNameMismatchModal = ({
  show,
  confirmCallback,
  orgSystemName,
  fileName,
}: ImportFileNameMismatchModalProps) => {
  const [t] = useTranslation('organizationImportCard');
  const [verifyText, setVerifyText] = useState('');
  return (
    <Modal
      show={show}
      title={t('importFileNameMismatchModalTitle')}
      width={660}
      content={
        <ImportModalContent data-cy="import-table-verify-modal">
          <DangerZoneWarning text={t('importFileNameMismatchModalError')} />
          <FileNameOrgNameWrapper>
            <BodyMedium color={theme.colors.text.secondary}>{t('importFileNameMismatchModalFileName')}:</BodyMedium>
            <BodyMedium bold>{fileName}</BodyMedium>
            <BodyMedium color={theme.colors.text.secondary} className="mt-7">
              {t('importFileNameMismatchModalOrganizationName')}:
            </BodyMedium>
            <BodyMedium data-cy="import-table-verify-org-name" bold>
              {orgSystemName}
            </BodyMedium>
          </FileNameOrgNameWrapper>
          <BodyMedium>{t('importFileNameMismatchModalText1')}</BodyMedium>
          <BodyMedium>{t('importFileNameMismatchModalText2')}</BodyMedium>
          <StyledFormGroup>
            <InputLabel>{t('importFileNameMismatchModalOrganizationName')}</InputLabel>
            <FormControl
              type="text"
              value={verifyText}
              onChange={(e) => {
                setVerifyText(e.target.value);
              }}
              isInvalid={!!verifyText && verifyText !== orgSystemName}
              data-cy="import-table-verify-org-name-input"
            />
            {!!verifyText && verifyText !== orgSystemName && (
              <FormErrorFeedback error={t('importFileNameMismatchModalWarning')} />
            )}
          </StyledFormGroup>
        </ImportModalContent>
      }
      buttons={{
        submit: {
          title: t('continueButton'),
          disabled: verifyText !== orgSystemName,
        },
        cancel: { title: t('common:close') },
      }}
      onHide={() => confirmCallback(false)}
      onSubmit={() => confirmCallback(true)}
    />
  );
};

const ImportModalContent = styled.div`
  margin-top: ${theme.spacing.h};
  color: ${theme.colors.text.primary};
`;

const FileNameOrgNameWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
  margin: ${theme.spacing.f} 0;
  padding: ${theme.spacing.g};
  border: 1px solid ${theme.colors.borderMain.default};
  border-radius: ${theme.borderRadius.l};
`;

const InputLabel = styled(FormGroupLabel)`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.typography.body.small.regular.fontSize};
  margin-top: ${theme.spacing.f};
  margin-left: ${theme.spacing.f};
  margin-bottom: ${theme.spacing.c};
`;

export const constructEmployees = <T extends boolean>(
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>,
  withIds: T,
): T extends true ? EmployeeRecordWithId[] : EmployeeRecord[] => {
  const employees = organizationTeams
    .flatMap((team) =>
      team.members.map((member) => ({
        firstname: member.firstname || '',
        surname: member.surname || '',
        team: team.name || '',
        email:
          member.contacts?.filter((c) => c.type === ContactType.Email && c.source === ContactSource.Primary)[0]
            ?.value || '',
        otherEmail:
          member.contacts?.filter((c) => c.type === ContactType.Email && c.source === ContactSource.Respondent)[0]
            ?.value || '',
        countryCallingCode:
          member.contacts?.filter((c) => c.type === ContactType.Sms)[0]?.phoneNumber?.countryCallingCode || '',
        phoneNumber: member.contacts?.filter((c) => c.type === ContactType.Sms)[0]?.phoneNumber?.nationalNumber || '',
        preferredContact: member.preferredContactType,
        surveyParticipant: member.disabled ? 'N' : 'Y',
        managerOfTeam: organizationTeams
          .filter((t) => t.leader?.id === member.id)
          .map((t) => t.name || '')
          .filter((name) => name !== ''),
        language: member.language?.code || '',
        ...(withIds ? { id: member.id } : {}),
      })),
    )
    .sort((a, b) => {
      const teamComparison = (a.team || 'ZZZ').localeCompare(b.team || 'ZZZ');
      if (teamComparison !== 0) return teamComparison;

      const surnameComparison = (a.surname || '').localeCompare(b.surname || '');
      if (surnameComparison !== 0) return surnameComparison;

      return (a.firstname || '').localeCompare(b.firstname || '');
    });

  return employees as T extends true ? EmployeeRecordWithId[] : EmployeeRecord[];
};

export const constructTeams = (
  organizationTeams: NonNullable<OrganizationTeamsQuery['organizationTeams']>,
): TeamRecord[] => {
  // Construct the team structure to get the hierarchy (teams under other teams)
  const teamsInStructure = constructTeamStructure(organizationTeams, () => '');
  // and then flatten it to get a list of all teams that is ordered based on the hierarchy
  const teams = flatTeams(teamsInStructure);
  const flattenTeams = teams
    .filter((team) => team.name)
    .map((team) => ({
      id: team.structureId ?? '',
      name: team.name || '',
      supervisorTeam:
        (team.supervisorTeam && organizationTeams.find((t) => t.id === team.supervisorTeam?.id))?.name || '',
    }));

  return flattenTeams;
};

const createAndDownloadOrganizationImportFile = (
  employees: EmployeeRecord[],
  teams: TeamRecord[],
  systemName: string,
  t: TFunction,
) => {
  const fileName = `${sanitize(systemName)}.xlsx`;

  const translateKeys = <T extends Record<string, any>>(items: T[], prefix: string): Record<string, string>[] => {
    return items.map((item) =>
      (Object.keys(item) as Array<keyof T>).reduce(
        (acc, key) => {
          let value = item[key];
          // Convert managerOfTeam array to semicolon-separated string for Excel
          if (key === 'managerOfTeam' && Array.isArray(value)) {
            value = value.join('; ');
          }
          acc[t(`${prefix}${(key as string).charAt(0).toUpperCase() + (key as string).slice(1)}`)] = value;
          return acc;
        },
        {} as Record<string, string>,
      ),
    );
  };

  const employeesWithTranslatedKeys = translateKeys(employees, 'employeeSheet');
  const teamsWithTranslatedKeys = translateKeys(teams, 'teamSheet');

  const helpRows = t('helpSheetRows')
    .split('\n')
    .map((row) => row.split('|'));
  const helpSheet = XLSX.utils.aoa_to_sheet(helpRows);
  helpSheet['!cols'] = [{ wch: 50 }, { wch: 120 }];

  const employeesSheet = XLSX.utils.json_to_sheet(employeesWithTranslatedKeys);
  employeesSheet['!cols'] = getCharacterCountOfColumns(employeesWithTranslatedKeys).map((count) => ({
    wch: count + 1,
  }));
  const teamsSheet = XLSX.utils.json_to_sheet(teamsWithTranslatedKeys);
  teamsSheet['!cols'] = getCharacterCountOfColumns(teamsWithTranslatedKeys).map((count) => ({
    wch: count + 1,
  }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, helpSheet, t('helpSheetTitle'));
  XLSX.utils.book_append_sheet(workbook, employeesSheet, t('employeeSheetTitle'));
  XLSX.utils.book_append_sheet(workbook, teamsSheet, t('teamSheetTitle'));
  XLSX.writeFile(workbook, fileName);
};

const getCharacterCountOfColumns = (data: Record<string, string>[]) => {
  const maxLength = data.reduce(
    (acc, item) => {
      Object.keys(item).forEach((key) => {
        acc[key] = Math.max(acc[key] || 0, item[key].length, key.length);
      });
      return acc;
    },
    {} as Record<string, number>,
  );
  return Object.values(maxLength);
};

export const getOrganizationImportColumnWidth = (columnId: string): number => {
  const columnWidths: Record<string, number> = {
    // Employee columns
    firstname: 80,
    surname: 80,
    team: 150,
    email: 200,
    otherEmail: 200,
    countryCallingCode: 75,
    phoneNumber: 90,
    preferredContact: 210,
    surveyParticipant: 160,
    managerOfTeam: 200,
    language: 100,

    // Team columns
    id: 140,
    name: 200,
    supervisorTeam: 200,
  };

  return columnWidths[columnId] || 100; // Default to 100 if column id not found
};
